import React, { useMemo, useState, useRef, useCallback } from "react";

// Visualizes question flow using a tidy tree layout and SVG connectors
const LogicViewer = ({ questions = [] }) => {
  const [mode, setMode] = useState("stack"); // 'stack' | 'flow'
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [search, setSearch] = useState("");
  const { nodes, edges, stats, svgHeight, svgWidth, groupKeyById, groupOrder } = useMemo(() => {
    const qList = Array.isArray(questions) ? questions : [];
    const nodes = qList.map((q, idx) => ({
      id: q.id,
      label: q.question_text || q.text || `Q${q.id}`,
      type: q.question_type || q.type,
      idx,
    }));

    const edges = [];
    const nextMap = new Map(); // id -> [{to,label}]
    qList.forEach((q) => {
      const arr = [];
      (q.answers || []).forEach((a) => {
        if (a.next_question_id) {
          edges.push({ from: q.id, to: a.next_question_id, label: a.answer_text || a.text });
          arr.push({ to: a.next_question_id, label: a.answer_text || a.text });
        }
      });
      nextMap.set(q.id, arr);
    });

    const outDegree = new Map();
    const inDegree = new Map();
    nodes.forEach((n) => {
      outDegree.set(n.id, 0);
      inDegree.set(n.id, 0);
    });
    edges.forEach((e) => {
      outDegree.set(e.from, (outDegree.get(e.from) || 0) + 1);
      inDegree.set(e.to, (inDegree.get(e.to) || 0) + 1);
    });

    // Build forest roots
    let roots = nodes.filter((n) => (inDegree.get(n.id) || 0) === 0);
    if (roots.length === 0 && nodes.length) {
      // fallback: pick smallest id as root
      const minNode = nodes.reduce((a, b) => (a.id < b.id ? a : b));
      roots = [minNode];
    }

    // Choose a primary parent for each node (first encountered)
    const primaryParent = new Map();
    edges.forEach((e) => {
      if (!primaryParent.has(e.to)) primaryParent.set(e.to, e.from);
    });

    // BFS layering for top-down levels
    const level = new Map();
    const visited = new Set();
    const queue = [];
    roots.forEach((r) => {
      level.set(r.id, 0);
      queue.push(r.id);
      visited.add(r.id);
    });

    while (queue.length) {
      const current = queue.shift();
      const curLvl = level.get(current) || 0;
      const children = nextMap.get(current) || [];
      children.forEach((ch) => {
        const p = primaryParent.get(ch.to) ?? current;
        if (!visited.has(ch.to)) {
          visited.add(ch.to);
          level.set(ch.to, (level.get(ch.to) ?? curLvl + 1));
          queue.push(ch.to);
        }
      });
    }

    // Unvisited nodes (separate components or cycles)
    nodes.forEach((n) => {
      if (!level.has(n.id)) level.set(n.id, 0);
    });

    // Group by level
    const levels = new Map();
    nodes.forEach((n) => {
      const l = level.get(n.id) || 0;
      if (!levels.has(l)) levels.set(l, []);
      levels.get(l).push(n);
    });
    // sort nodes and roots by id for stable layout
    Array.from(levels.values()).forEach((arr) => arr.sort((a, b) => a.id - b.id));
    roots.sort((a, b) => a.id - b.id);

    // Layout constants and positioning
    let nodeWidth = 240;
    let nodeHeight = 90;
    let ySpacing = 120;
    const topMargin = 24;
    let canvasWidth = 1200;

    const totalLevels = Math.max(1, ...Array.from(levels.keys())) + 1;
    const maxPerLevel = Math.max(1, ...Array.from(levels.values()).map((arr) => arr.length));
    const pos = new Map();

    // Build header groups for flow mode
    const groupKeyById = new Map();
    const groups = new Map();
    qList.forEach((q) => {
      const key = q.headerName || "Ungrouped";
      groupKeyById.set(q.id, key);
    });
    nodes.forEach((n) => {
      const key = groupKeyById.get(n.id) || "Ungrouped";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(n);
    });
    const groupOrder = Array.from(groups.keys()).sort((a, b) => {
      if (a === "Ungrouped") return -1;
      if (b === "Ungrouped") return 1;
      return a.localeCompare(b);
    });
    groupOrder.forEach((g) => groups.get(g).sort((a, b) => a.id - b.id));

    if (mode === "stack") {
      // Compact top-to-bottom stacking sorted by ID
      const sorted = [...nodes].sort((a, b) => a.id - b.id);
      nodeWidth = 720;
      nodeHeight = 84;
      ySpacing = 110;
      canvasWidth = Math.max(900, nodeWidth + 200);
      sorted.forEach((n, i) => {
        const x = canvasWidth / 2 - nodeWidth / 2;
        const y = topMargin + i * ySpacing;
        pos.set(n.id, { x, y });
      });
    } else {
      // Flow mode: columns by header group, rows by question id
      nodeWidth = 300;
      nodeHeight = 90;
      ySpacing = 110;
      const colGap = 120;
      const leftMargin = 40;
      canvasWidth = leftMargin + groupOrder.length * (nodeWidth + colGap);

      groupOrder.forEach((g, gi) => {
        const colX = leftMargin + gi * (nodeWidth + colGap);
        const arr = groups.get(g) || [];
        arr.forEach((n, ri) => {
          const x = colX;
          const y = topMargin + ri * ySpacing;
          pos.set(n.id, { x, y });
        });
      });
    }

    const stats = {
      totalQuestions: nodes.length,
      totalEdges: edges.length,
      startNodes: nodes.filter((n) => (inDegree.get(n.id) || 0) === 0).map((n) => n.id),
      endNodes: nodes.filter((n) => (outDegree.get(n.id) || 0) === 0).map((n) => n.id),
      orphans: nodes.filter((n) => (inDegree.get(n.id) || 0) === 0 && (outDegree.get(n.id) || 0) === 0).map((n) => n.id),
    };

    const tallestCol = Math.max(
      1,
      ...(mode === "flow"
        ? groupOrder.map((g) => (groups.get(g) || []).length)
        : [nodes.length])
    );
    const svgHeight = topMargin + tallestCol * ySpacing + nodeHeight + 40;

    return {
      nodes: nodes.map((n) => ({ ...n, ...pos.get(n.id), nodeWidth, nodeHeight })),
      edges,
      stats,
      svgHeight,
      svgWidth: canvasWidth,
      groupKeyById,
      groupOrder,
    };
  }, [questions, mode]);

  const zoomIn = () => setScale((s) => Math.min(2.5, +(s + 0.1).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.4, +(s - 0.1).toFixed(2)));
  const resetView = () => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    setSelectedNode(null);
    setHoveredNode(null);
  };

  const onMouseDown = useCallback((e) => {
    if (e.target.tagName === 'svg' || e.target.tagName === 'path') {
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  }, [pan]);
  const onMouseMove = useCallback((e) => {
    if (!isPanning) return;
    setPan({ x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y });
  }, [isPanning]);
  const onMouseUp = useCallback(() => setIsPanning(false), []);

  const highlighted = useMemo(() => {
    const nodeIds = new Set();
    const edgeIdx = new Set();
    const focusId = selectedNode ?? hoveredNode;
    if (focusId != null) {
      nodes.forEach((n) => { if (n.id === focusId) nodeIds.add(n.id); });
      edges.forEach((e, i) => {
        if (e.from === focusId) {
          edgeIdx.add(i);
          nodeIds.add(e.to);
        }
      });
    }
    const q = (search || "").toLowerCase();
    if (q) {
      nodes.forEach((n) => {
        if (String(n.id).includes(q) || (n.label || "").toLowerCase().includes(q)) nodeIds.add(n.id);
      });
    }
    return { nodeIds, edgeIdx };
  }, [nodes, edges, selectedNode, hoveredNode, search]);

  return (
    <div>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-xs text-gray-500">Total Questions</div>
          <div className="text-lg font-semibold">{stats.totalQuestions}</div>
        </div>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-xs text-gray-500">Total Links</div>
          <div className="text-lg font-semibold">{stats.totalEdges}</div>
        </div>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-xs text-gray-500">Start Nodes</div>
          <div className="text-lg font-semibold">{stats.startNodes.length}</div>
        </div>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-xs text-gray-500">End Nodes</div>
          <div className="text-lg font-semibold">{stats.endNodes.length}</div>
        </div>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-xs text-gray-500">Orphans</div>
          <div className="text-lg font-semibold">{stats.orphans.length}</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">View:</span>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
            className={"px-3 py-1.5 text-sm " + (mode === "stack" ? "bg-indigo-600 text-white" : "bg-white text-gray-700")}
              onClick={() => setMode("stack")}
            >
              Stack
            </button>
            <button
            className={"px-3 py-1.5 text-sm border-l border-gray-200 " + (mode === "flow" ? "bg-indigo-600 text-white" : "bg-white text-gray-700")}
            onClick={() => setMode("flow")}
            >
            Flow
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Zoom:</span>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button className="px-3 py-1.5 text-sm bg-white text-gray-700" onClick={zoomOut}>−</button>
            <div className="px-2 py-1.5 text-sm bg-gray-50 text-gray-700 min-w-[56px] text-center">{Math.round(scale*100)}%</div>
            <button className="px-3 py-1.5 text-sm bg-white text-gray-700" onClick={zoomIn}>+</button>
          </div>
          <button className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded" onClick={resetView}>Reset</button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ID or text..."
            className="px-3 py-1.5 border border-gray-300 rounded w-64"
          />
        </div>
      </div>

      <div
        className="relative w-full overflow-auto"
        style={{ minHeight: 600 }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <svg width={svgWidth} height={Math.max(800, svgHeight)} style={mode === "stack" ? { display: 'block', margin: '0 auto' } : undefined}>
          <defs>
            <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
            <marker id="arrow" markerWidth="10" markerHeight="10" refX="10" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="#14b8a6" />
            </marker>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#94a3b8" floodOpacity="0.4" />
            </filter>
          </defs>

          <g transform={`translate(${pan.x}, ${pan.y}) scale(${scale})`}>
          {(mode === 'flow' || mode === 'stack') && (() => {
            // compute group bounds for background containers
            const pad = 16;
            const boundsMap = new Map();
            nodes.forEach((n) => {
              const key = (groupKeyById && groupKeyById.get) ? (groupKeyById.get(n.id) || 'Ungrouped') : 'Ungrouped';
              const b = boundsMap.get(key) || { minX: n.x, minY: n.y, maxX: n.x + n.nodeWidth, maxY: n.y + n.nodeHeight };
              b.minX = Math.min(b.minX, n.x);
              b.minY = Math.min(b.minY, n.y);
              b.maxX = Math.max(b.maxX, n.x + n.nodeWidth);
              b.maxY = Math.max(b.maxY, n.y + n.nodeHeight);
              boundsMap.set(key, b);
            });
            const ordered = Array.from(boundsMap.entries());
            return ordered.map(([name, b], i) => (
              <g key={i}>
                <rect x={b.minX - pad} y={b.minY - pad * 1.5} width={(b.maxX - b.minX) + pad * 2} height={(b.maxY - b.minY) + pad * 2.5} rx="12" ry="12" fill="#f8fafc" stroke="#e2e8f0" />
                <text x={b.minX} y={b.minY - 6} fontSize="12" fontWeight="700" fill="#475569">{name}</text>
              </g>
            ));
          })()}
          {/* Edges */}
          {(() => {
            // Build display edges, add synthetic sequential edges in flow mode for orphans
            const displayEdges = [...edges];
            if (mode === "flow") {
              const inD = new Map();
              const outD = new Map();
              nodes.forEach((n) => { inD.set(n.id, 0); outD.set(n.id, 0); });
              edges.forEach((e) => { inD.set(e.to, (inD.get(e.to) || 0) + 1); outD.set(e.from, (outD.get(e.from) || 0) + 1); });
              const byGroup = new Map();
              nodes.forEach((n) => {
                const g = groupKeyById.get(n.id) || "Ungrouped";
                if (!byGroup.has(g)) byGroup.set(g, []);
                byGroup.get(g).push(n);
              });
              groupOrder.forEach((g) => byGroup.get(g)?.sort((a, b) => a.id - b.id));
              groupOrder.forEach((g) => {
                const arr = byGroup.get(g) || [];
                for (let i = 0; i < arr.length; i++) {
                  const n = arr[i];
                  const isOrphan = (inD.get(n.id) || 0) === 0 && (outD.get(n.id) || 0) === 0;
                  if (isOrphan && i + 1 < arr.length) {
                    displayEdges.push({ from: n.id, to: arr[i + 1].id, label: "", synthetic: true });
                  }
                }
              });
            }
            return displayEdges.map((e, i) => {
              const from = nodes.find((n) => n.id === e.from);
              const to = nodes.find((n) => n.id === e.to);
              if (!from || !to) return null;
              // Start and end from the right sides of both nodes
              const sx = from.x + from.nodeWidth; // right side of source
              const sy = from.y + from.nodeHeight / 2;
              const tx = to.x + to.nodeWidth; // right side of target
              const ty = to.y + to.nodeHeight / 2;
              const midX = Math.max(sx, tx) + Math.max(80, Math.abs(tx - sx) / 2);
              const path = `M ${sx} ${sy} C ${midX} ${sy}, ${midX} ${ty}, ${tx} ${ty}`;
              const mx = (midX + tx) / 2;
              const my = (sy + ty) / 2;
              const isHi = highlighted.edgeIdx.has(i) || highlighted.nodeIds.has(from.id) || highlighted.nodeIds.has(to.id);
              return (
                <g key={i} opacity={highlighted.nodeIds.size ? (isHi ? 1 : 0.25) : 1}>
                  <path d={path} stroke="url(#edgeGrad)" strokeWidth={isHi ? 2.5 : 1.5} fill="none" markerEnd="url(#arrow)" />
                  {e.label && isHi && (
                    <g>
                      <rect x={mx - 60} y={my - 12} width="120" height="22" rx="6" fill="#0ea5e9" opacity="0.95" />
                      <text x={mx} y={my + 4} fontSize="11" fontWeight="600" fill="#ffffff" textAnchor="middle">
                        {e.label}
                      </text>
                    </g>
                  )}
                </g>
              );
            });
          })()}

          {/* Nodes */}
          {nodes.map((n) => {
            const isStart = stats.startNodes.includes(n.id);
            const isEnd = stats.endNodes.includes(n.id);
            const isOrphan = stats.orphans.includes(n.id);
            const fill = isStart ? "#ecfeff" : isEnd ? "#f0fdf4" : "#f8fafc";
            const stroke = isOrphan ? "#f59e0b" : isStart ? "#06b6d4" : isEnd ? "#22c55e" : "#cbd5e1";
            const dash = isOrphan ? "4,3" : undefined;
            const isHi = highlighted.nodeIds.size ? highlighted.nodeIds.has(n.id) : true;
            return (
              <g
                key={n.id}
                transform={`translate(${n.x}, ${n.y})`}
                filter="url(#shadow)"
                opacity={isHi ? 1 : 0.35}
                onMouseEnter={() => setHoveredNode(n.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => setSelectedNode((prev) => (prev === n.id ? null : n.id))}
              >
                <rect width={n.nodeWidth} height={n.nodeHeight} rx="10" ry="10" fill={fill} stroke={stroke} strokeDasharray={dash} />
                <text x="12" y="20" fontSize="12.5" fontWeight="700" fill="#111827">{`Q${n.id}`} · {n.type}</text>
                <foreignObject x="12" y="28" width={n.nodeWidth - 24} height={n.nodeHeight - 36}>
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: 12, color: '#334155', lineHeight: '16px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {n.label}
                  </div>
                </foreignObject>
              </g>
            );
          })}
          </g>
        </svg>
      </div>

      {stats.orphans.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
          Orphan questions with no incoming or outgoing links: {stats.orphans.join(", ")}
        </div>
      )}
    </div>
  );
};

export default LogicViewer;


